import { ReactElement, useCallback, useMemo, useState } from 'react'
import { useIsOrganisationAdmin } from '@hooks'
import { filter, isFunction, reduce } from 'lodash-es'
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
} from '@mui/material'
import { UseMutationResult } from '@tanstack/react-query'
import { SelectableButton } from '@ui/actions'
import { useSnackbar } from '@ui/snackbar'
import { Box } from '@ui/structure'
import { getDefaultShortName, getShortNameForCopy } from 'src/utils/assemblies'
import { assemblySchema } from '../AssemblyForm/schema'
import AssemblyListItem from '../AssemblyListItem'
import CreateDialog from '../CreateAssemblyDialog'
import EditAssemblyDialog from '../EditAssemblyDialog'
import PasteAssemblyFromClipboardButton from '../PasteAssemblyFromClipboardButton'
import { assemblyTypes, assemblyKinds } from '../constants'
import { serializeAssemblyForClipboard } from '../utils/clipboardAssembly'
import {
  getAllowedUsageTypesForDuplication,
  prepareAssemblyForDuplication,
} from '../utils/duplicateAssembly'

interface Props {
  assemblies: Assembly[]
  onDelete: (assembly: Assembly) => void
  onDownload: (assembly: Assembly) => Promise<void>
  onSetStandard: (assembly: Assembly) => void
  isSettingStandard: boolean
  onStore?: (assembly: Assembly) => Promise<CLTAssembly | TimberFrameAssembly>
  createMutation: UseMutationResult<CLTAssembly | TimberFrameAssembly, unknown, Assembly, unknown>
  editMutation: UseMutationResult<CLTAssembly | TimberFrameAssembly, unknown, Assembly, unknown>
  earthMassForce: number
}

type AssembliesByType = {
  [key in AssemblyTypes]?: Assembly[]
}

const AssemblyList = ({
  assemblies,
  onDelete,
  onDownload,
  onSetStandard,
  isSettingStandard,
  onStore,
  createMutation,
  editMutation,
  earthMassForce,
}: Props): ReactElement => {
  // This list shows the organisation's library: every member sees it, only an
  // admin of that organisation may change it. Assume read-only until we know
  // otherwise, so no control is offered that the backend would refuse.
  const { isOrganisationAdmin } = useIsOrganisationAdmin()
  const readOnly = !isOrganisationAdmin
  const { enqueueSnackbar } = useSnackbar()
  const [createValues, setCreateValues] = useState<Assembly | null>(null)
  const [createAllowedUsageTypes, setCreateAllowedUsageTypes] = useState<
    AssemblyTypes[] | undefined
  >(undefined)
  const [editValues, setEditValues] = useState<Assembly | null>(null)

  const handleSetCreateValues = (assembly: Assembly, allowedUsageTypes?: AssemblyTypes[]) => {
    setCreateValues(assembly)
    setCreateAllowedUsageTypes(allowedUsageTypes)
  }

  const assemblyKindOptionsPerUsageType = useMemo(() => {
    return reduce(
      assemblyTypes,
      (col, assemblyType) => ({
        ...col,
        [assemblyType.usage_type]: filter(assemblyKinds, kind =>
          kind.enabledOn.includes(assemblyType.usage_type),
        ).map(({ label, type }) => ({ label, value: type })),
      }),
      {} as Record<AssemblyTypes, { value: string; label: string }[]>,
    )
  }, [])

  const allAssemblies = useMemo(() => assemblies || [], [assemblies])

  const assembliesByType = useMemo(
    () =>
      allAssemblies.reduce<AssembliesByType>((result, assembly) => {
        const { usage_type } = assembly

        result[usage_type] = [...(result[usage_type] || []), assembly]

        return result
      }, {}),
    [allAssemblies],
  )

  const handleCopy = useCallback(
    (assembly: Assembly) => {
      try {
        navigator.clipboard.writeText(serializeAssemblyForClipboard(assembly))
        enqueueSnackbar('Aufbau in Zwischenablage kopiert', { variant: 'success' })
      } catch (error) {
        enqueueSnackbar('Fehler beim Kopieren des Aufbaus', { variant: 'error' })
      }
    },
    [enqueueSnackbar],
  )

  const handlePasteAssembly = useCallback(
    (assembly: Assembly, targetUsageType: AssemblyTypes) => {
      const allowedUsageTypes = getAllowedUsageTypesForDuplication(assembly.usage_type)
      const duplicated = prepareAssemblyForDuplication(assembly, targetUsageType)
      setCreateValues({ ...duplicated, short_name: getShortNameForCopy(duplicated, allAssemblies) })
      setCreateAllowedUsageTypes(allowedUsageTypes)
    },
    [allAssemblies],
  )

  return (
    <>
      <Box>
        {assemblyTypes.map(({ label, usage_type }) => {
          const assemblies = assembliesByType[usage_type] || []
          const hasAssemblies = !!assemblies.length
          const SelectButton = (
            <SelectableButton
              onClick={({ value: kind }: { value: AssemblyKind }) =>
                setCreateValues({
                  ...assemblySchema.getDefault(),
                  usage_type,
                  kind,
                  isStandard: false,
                  short_name: getDefaultShortName(usage_type, allAssemblies),
                })
              }
              options={assemblyKindOptionsPerUsageType[usage_type]}
              buttonProps={{
                variant: 'outlined',
                color: 'primary',
                size: 'small',
                sx: {
                  alignSelf: 'center',
                },
              }}
              data-cy={`${usage_type}-select`}
            >
              anlegen
            </SelectableButton>
          )

          const CreateActions = !readOnly && (
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
              <PasteAssemblyFromClipboardButton
                usageType={usage_type}
                onPaste={assembly => handlePasteAssembly(assembly, usage_type)}
              />
              {SelectButton}
            </Stack>
          )

          return (
            <Box mb={3} key={usage_type}>
              <Box display="flex" mb={1} justifyContent="space-between" alignItems="center">
                <Typography variant="h6" mb={1}>
                  {label} {`(${assemblies.length})`}
                </Typography>
                {hasAssemblies && CreateActions}
              </Box>
              {!hasAssemblies && (
                <Paper>
                  <Box padding={3} textAlign="center">
                    <Typography>
                      {readOnly
                        ? `Es sind noch keine Aufbauten für ${label} angelegt`
                        : `Sie haben noch keine Aufbauten für ${label} angelegt`}
                    </Typography>
                    {CreateActions && <Box mt={2}>{CreateActions}</Box>}
                  </Box>
                </Paper>
              )}
              {hasAssemblies && (
                <TableContainer component={Paper}>
                  <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <TableCell align="center">Std.</TableCell>
                        <TableCell>Typ</TableCell>
                        <TableCell>Illustration</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Kurzbez.</TableCell>
                        <TableCell align="center">Gewicht [kN/m2]*</TableCell>
                        <TableCell align="right">Aktionen</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {assemblies.map((assembly, index) => {
                        const { usage_type } = assembly

                        return (
                          <AssemblyListItem
                            key={assembly.guid}
                            assembly={assembly}
                            onDelete={() => onDelete(assembly)}
                            onDownload={() => onDownload(assembly)}
                            onDuplicate={() => {
                              const allowedUsageTypes = getAllowedUsageTypesForDuplication(
                                assembly.usage_type,
                              )
                              const duplicated = {
                                ...assembly,
                                guid: '', // Clear GUID for new assembly
                                short_name: getShortNameForCopy(assembly, allAssemblies),
                              }
                              handleSetCreateValues(duplicated, allowedUsageTypes)
                            }}
                            onEdit={() => setEditValues(assembly)}
                            onSetStandard={() => onSetStandard(assembly)}
                            isSettingStandard={isSettingStandard}
                            onStore={isFunction(onStore) ? () => onStore(assembly) : undefined}
                            data-cy={`${usage_type}-list-item-${index}`}
                            earthMassForce={earthMassForce}
                            onCopy={() => handleCopy(assembly)}
                          />
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )
        })}
      </Box>
      {createValues && (
        <CreateDialog
          mutation={createMutation}
          open
          onClose={() => {
            setCreateValues(null)
            setCreateAllowedUsageTypes(undefined)
          }}
          assembly={createValues}
          earthMassForce={earthMassForce}
          allowedUsageTypes={createAllowedUsageTypes}
        />
      )}
      {editValues && (
        <EditAssemblyDialog
          mutation={editMutation}
          open
          onClose={() => setEditValues(null)}
          assembly={editValues}
          earthMassForce={earthMassForce}
        />
      )}
    </>
  )
}

export default AssemblyList
